import React, { useCallback, useMemo, useState } from "react";
import {
  CalendarOff,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import {
  HomeworkCalendarAssignment,
  HomeworkCalendarStatus,
} from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import { PortalLink } from "../components/PortalLink";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  ClientPrimaryTabs,
  SegmentedNavigation,
} from "../components/PortalNavigation";
import {
  clientHomeworkDetailPath,
  clientWorkspacePath,
  insightsNavigation,
} from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";
import {
  readTeamClinicId,
  withTeamClinicScope,
} from "../utils/teamClinicScope";

const TIMEZONE = "America/Los_Angeles";
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_MS = 86_400_000;
type CalendarView = "month" | "week";

function dateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function monthFromKey(value: string | null): Date | null {
  const match = value?.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return new Date(Date.UTC(year, month - 1, 1));
}

function dateFromKey(value: string | null): Date | null {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  return dateKey(date) === value ? date : null;
}

function startOfWeek(date: Date): Date {
  return new Date(date.getTime() - date.getUTCDay() * DAY_MS);
}

function weekDates(weekStart: Date): Date[] {
  return Array.from(
    { length: 7 },
    (_, index) => new Date(weekStart.getTime() + index * DAY_MS),
  );
}

function todayInLosAngeles(): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .formatToParts(new Date())
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function todayDateInLosAngeles(): Date {
  const today = todayInLosAngeles();
  return new Date(
    Date.UTC(
      Number(today.slice(0, 4)),
      Number(today.slice(5, 7)) - 1,
      Number(today.slice(8, 10)),
    ),
  );
}

function initialCalendarState(): {
  view: CalendarView;
  month: Date;
  weekStart: Date;
  selectedDate: string | null;
} {
  const parameters = new URLSearchParams(window.location.search);
  const todayDate = todayDateInLosAngeles();
  const view: CalendarView =
    parameters.get("view") === "week" ? "week" : "month";
  const requestedDate = dateFromKey(parameters.get("date"));
  const requestedMonth = monthFromKey(parameters.get("month"));
  const anchor = requestedDate ?? requestedMonth ?? todayDate;
  const month = new Date(
    Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1),
  );
  const monthDates = gridDates(month);
  const monthStart = dateKey(monthDates[0]);
  const monthEnd = dateKey(monthDates[monthDates.length - 1]);
  const today = dateKey(todayDate);

  return {
    view,
    month,
    weekStart: startOfWeek(anchor),
    selectedDate:
      view === "week"
        ? dateKey(anchor)
        : today >= monthStart && today <= monthEnd
          ? today
          : null,
  };
}

function gridDates(month: Date): Date[] {
  const first = new Date(
    Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), 1),
  );
  const start = first.getTime() - first.getUTCDay() * DAY_MS;
  return Array.from(
    { length: 42 },
    (_, index) => new Date(start + index * DAY_MS),
  );
}

function formatMonth(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatWeekRange(dates: readonly Date[]): string {
  const start = dates[0];
  const end = dates[dates.length - 1];
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();
  const format = (date: Date, options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat("en-US", { timeZone: "UTC", ...options }).format(
      date,
    );

  if (sameMonth) {
    return `${format(start, { month: "long" })} ${start.getUTCDate()}–${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  }
  if (sameYear) {
    return `${format(start, { month: "short", day: "numeric" })}–${format(end, { month: "short", day: "numeric" })}, ${end.getUTCFullYear()}`;
  }
  return `${format(start, { month: "short", day: "numeric", year: "numeric" })}–${format(end, { month: "short", day: "numeric", year: "numeric" })}`;
}

function formatLongDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(`${value}T00:00:00.000Z`));
}

function detailSource(status: HomeworkCalendarStatus) {
  return status === "Due" ? "current-homework" : "homework-history";
}

function statusDetail(assignment: HomeworkCalendarAssignment): string {
  if (assignment.calendarStatus === "Completed") return "Completed";
  if (assignment.calendarStatus === "Attempted") return "Attempted";
  if (assignment.calendarStatus === "Missed") return "Missed due date";
  return "Due date";
}

export function PracticeCalendarPage({
  clinicianCode,
  patientId,
}: {
  clinicianCode: string;
  patientId: string;
}) {
  const workspace = useClientWorkspace();
  const [initialState] = useState(initialCalendarState);
  const [calendarView, setCalendarView] = useState<CalendarView>(
    initialState.view,
  );
  const [visibleMonth, setVisibleMonth] = useState(initialState.month);
  const [visibleWeekStart, setVisibleWeekStart] = useState(
    initialState.weekStart,
  );
  const today = useMemo(todayInLosAngeles, []);
  const dates = useMemo(
    () =>
      calendarView === "month"
        ? gridDates(visibleMonth)
        : weekDates(visibleWeekStart),
    [calendarView, visibleMonth, visibleWeekStart],
  );
  const startDate = dateKey(dates[0]);
  const endDate = dateKey(dates[dates.length - 1]);
  const [selectedDate, setSelectedDate] = useState<string | null>(
    initialState.selectedDate,
  );
  const [isUnscheduledOpen, setIsUnscheduledOpen] = useState(false);
  const request = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getHomeworkCalendar(
            clinicianCode,
            patientId,
            startDate,
            endDate,
            signal,
            "team",
          )
        : clinicianPortalApi.getHomeworkCalendar(
            clinicianCode,
            patientId,
            startDate,
            endDate,
            signal,
          ),
    [clinicianCode, patientId, startDate, endDate, workspace.accessScope],
  );
  const state = usePortalData(request);
  const response =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const data =
    response?.range.startDate === startDate &&
    response.range.endDate === endDate
      ? response
      : undefined;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist &&
      data.patient?.id === patientId &&
      data.timezone === TIMEZONE,
  );
  const snapshot = getClinicianPortalClientSnapshot(
    patientId,
    workspace.accessScope,
  );
  const patient = available ? data?.patient : snapshot?.patient;
  const therapist = available ? data?.therapist : snapshot?.therapist;
  const homeworkByDate = useMemo(() => {
    const grouped = new Map<string, HomeworkCalendarAssignment[]>();
    for (const assignment of available ? (data?.homework ?? []) : []) {
      const existing = grouped.get(assignment.calendarDate) ?? [];
      existing.push(assignment);
      grouped.set(assignment.calendarDate, existing);
    }
    return grouped;
  }, [available, data?.homework]);
  const selectedAssignments = selectedDate
    ? (homeworkByDate.get(selectedDate) ?? [])
    : [];
  const currentHomeworkPath = (homeworkAssignmentId?: string): string => {
    const suffix = homeworkAssignmentId
      ? "current-homework/" + encodeURIComponent(homeworkAssignmentId)
      : "current-homework";
    const path = clientWorkspacePath(patientId, suffix, workspace.source);
    return workspace.source === "team"
      ? withTeamClinicScope(path, readTeamClinicId())
      : path;
  };

  const replaceCalendarUrl = (view: CalendarView, anchorDate: string): void => {
    const url = new URL(window.location.href);
    if (view === "week") {
      url.searchParams.set("view", "week");
      url.searchParams.set("date", anchorDate);
      url.searchParams.delete("month");
    } else {
      url.searchParams.delete("view");
      url.searchParams.delete("date");
      url.searchParams.set("month", anchorDate.slice(0, 7));
    }
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  };

  const changePeriod = (offset: number) => {
    if (calendarView === "week") {
      const nextWeekStart = new Date(
        visibleWeekStart.getTime() + offset * 7 * DAY_MS,
      );
      const nextSelectedDate = selectedDate
        ? dateKey(
            new Date(
              (dateFromKey(selectedDate)?.getTime() ??
                visibleWeekStart.getTime()) +
                offset * 7 * DAY_MS,
            ),
          )
        : dateKey(nextWeekStart);
      setVisibleWeekStart(nextWeekStart);
      setSelectedDate(nextSelectedDate);
      replaceCalendarUrl("week", nextSelectedDate);
      return;
    }

    const next = new Date(
      Date.UTC(
        visibleMonth.getUTCFullYear(),
        visibleMonth.getUTCMonth() + offset,
        1,
      ),
    );
    setVisibleMonth(next);
    setSelectedDate(null);
    replaceCalendarUrl("month", dateKey(next));
  };

  const changeCalendarView = (nextView: CalendarView) => {
    if (nextView === calendarView) return;
    const anchorKey =
      selectedDate ??
      (today >= startDate && today <= endDate
        ? today
        : calendarView === "month"
          ? dateKey(visibleMonth)
          : dateKey(visibleWeekStart));
    const anchor = dateFromKey(anchorKey) ?? todayDateInLosAngeles();

    if (nextView === "week") {
      setVisibleWeekStart(startOfWeek(anchor));
      setSelectedDate(anchorKey);
    } else {
      setVisibleMonth(
        new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1)),
      );
      setSelectedDate(anchorKey);
    }
    setCalendarView(nextView);
    replaceCalendarUrl(nextView, anchorKey);
  };

  const goToToday = () => {
    const todayDate = dateFromKey(today) ?? todayDateInLosAngeles();
    if (calendarView === "week") {
      setVisibleWeekStart(startOfWeek(todayDate));
    } else {
      setVisibleMonth(
        new Date(
          Date.UTC(todayDate.getUTCFullYear(), todayDate.getUTCMonth(), 1),
        ),
      );
    }
    setSelectedDate(today);
    replaceCalendarUrl(calendarView, today);
  };

  const selectDate = (key: string) => {
    setSelectedDate(key);
    if (calendarView === "week") replaceCalendarUrl("week", key);
  };

  const periodLabel =
    calendarView === "month"
      ? formatMonth(visibleMonth)
      : formatWeekRange(dates);

  const loadingCurrentRange =
    state.status === "loading" || (state.status === "refreshing" && !data);

  return (
    <ClinicianPortalLayout
      activeGlobalKey={workspace.source === "team" ? "team" : "clients"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        density="compact"
        title={
          patient
            ? `Client Record: ${patient.accessCode} • ${patient.nickname}`
            : "Client Record"
        }
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={clientWorkspaceBreadcrumbs(
              workspace,
              patientId,
              patient?.nickname || "Client",
            )}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="insights"
        />
      </PortalHeader>
      <div className="cp-page-content cp-page-content--profile cp-page-content--compact-client cp-page-content--sticky-secondary-nav">
        <SegmentedNavigation
          items={insightsNavigation}
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="practice-calendar"
        />

        <section
          className="cp-practice-calendar"
          aria-labelledby="practice-calendar-heading"
        >
          <div className="cp-content-header cp-content-header--route-intro">
            <div>
              <h2 id="practice-calendar-heading" className="cp-visually-hidden">
                Practice Calendar
              </h2>
              <p>Review this client's homework practice and due dates.</p>
            </div>
            <div className="cp-content-header__actions">
              <div
                className="cp-practice-calendar__view-toggle"
                role="group"
                aria-label="Calendar view"
              >
                {(["month", "week"] as const).map((view) => (
                  <button
                    type="button"
                    aria-pressed={calendarView === view}
                    key={view}
                    onClick={() => changeCalendarView(view)}
                  >
                    {view === "month" ? "Month" : "Week"}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="cp-button cp-button--secondary"
                onClick={goToToday}
              >
                Today
              </button>
              <button
                type="button"
                className="cp-button cp-button--secondary"
                disabled={state.status === "refreshing"}
                onClick={state.refresh}
              >
                <RefreshCw aria-hidden="true" />
                {state.status === "refreshing" ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>

          {loadingCurrentRange ? (
            <LoadingState label="Loading Practice Calendar" />
          ) : null}
          {state.status === "error" ? (
            <ErrorState error={state.error} onRetry={state.refresh} />
          ) : null}
          {data && !available ? (
            <ErrorState
              error={new Error("Practice Calendar data is not available")}
              onRetry={state.refresh}
            />
          ) : null}

          {available ? (
            <>
              {data.unscheduledCount > 0 ? (
                <section
                  className="cp-practice-calendar__unscheduled"
                  aria-labelledby="practice-calendar-unscheduled-heading"
                >
                  <div className="cp-practice-calendar__unscheduled-header">
                    <button
                      type="button"
                      className="cp-practice-calendar__unscheduled-toggle"
                      aria-expanded={isUnscheduledOpen}
                      aria-controls="practice-calendar-unscheduled-list"
                      onClick={() => setIsUnscheduledOpen((open) => !open)}
                    >
                      <CalendarOff aria-hidden="true" />
                      <span className="cp-practice-calendar__unscheduled-copy">
                        <strong id="practice-calendar-unscheduled-heading">
                          Unscheduled homework
                        </strong>
                        <span>
                          {data.unscheduledCount} homework{" "}
                          {data.unscheduledCount === 1
                            ? "assignment"
                            : "assignments"}{" "}
                          without a due date
                        </span>
                      </span>
                      {isUnscheduledOpen ? (
                        <ChevronDown aria-hidden="true" />
                      ) : (
                        <ChevronRight aria-hidden="true" />
                      )}
                    </button>
                    <PortalLink
                      className="cp-practice-calendar__unscheduled-current-link"
                      clinicianCode={clinicianCode}
                      path={currentHomeworkPath()}
                    >
                      View in Current Homework
                    </PortalLink>
                  </div>
                  {isUnscheduledOpen ? (
                    <ul
                      className="cp-practice-calendar__unscheduled-list"
                      id="practice-calendar-unscheduled-list"
                    >
                      {data.unscheduledHomework.map((assignment) => (
                        <li
                          className="cp-practice-calendar__unscheduled-item"
                          key={assignment.recordId}
                        >
                          <PortalLink
                            className="cp-practice-calendar__unscheduled-homework"
                            clinicianCode={clinicianCode}
                            path={currentHomeworkPath(assignment.recordId)}
                          >
                            <strong>{assignment.activityName}</strong>
                            <span>
                              {assignment.activityType ||
                                "Activity type not provided"}
                            </span>
                          </PortalLink>
                          <span className="cp-practice-calendar__unscheduled-badge">
                            No due date
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              ) : null}

              <div className="cp-practice-calendar__card">
                <div className="cp-practice-calendar__toolbar">
                  <button
                    type="button"
                    className="cp-practice-calendar__period-button"
                    aria-label={`Previous ${calendarView}`}
                    onClick={() => changePeriod(-1)}
                  >
                    <ChevronLeft aria-hidden="true" />
                  </button>
                  <h3 aria-live="polite">{periodLabel}</h3>
                  <button
                    type="button"
                    className="cp-practice-calendar__period-button"
                    aria-label={`Next ${calendarView}`}
                    onClick={() => changePeriod(1)}
                  >
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>

                <div
                  className="cp-practice-calendar__legend"
                  aria-label="Calendar status legend"
                >
                  {(["Completed", "Attempted", "Due", "Missed"] as const).map(
                    (status) => (
                      <span key={status}>
                        <span
                          className="cp-practice-calendar__dot"
                          data-status={status.toLowerCase()}
                          aria-hidden="true"
                        />
                        {status}
                      </span>
                    ),
                  )}
                </div>

                <div
                  className="cp-practice-calendar__grid"
                  data-view={calendarView}
                  aria-label={`${periodLabel} calendar`}
                >
                  {WEEKDAYS.map((weekday) => (
                    <div
                      className="cp-practice-calendar__weekday"
                      key={weekday}
                    >
                      {weekday}
                    </div>
                  ))}
                  {dates.map((date) => {
                    const key = dateKey(date);
                    const assignments = homeworkByDate.get(key) ?? [];
                    const outsideMonth =
                      calendarView === "month" &&
                      date.getUTCMonth() !== visibleMonth.getUTCMonth();
                    const dayLabel = `${formatLongDate(key)}, ${assignments.length} homework ${assignments.length === 1 ? "assignment" : "assignments"}`;

                    if (calendarView === "week") {
                      return (
                        <div
                          className="cp-practice-calendar__day"
                          data-selected={selectedDate === key || undefined}
                          key={key}
                        >
                          <button
                            type="button"
                            className="cp-practice-calendar__day-select"
                            aria-pressed={selectedDate === key}
                            aria-label={dayLabel}
                            onClick={() => selectDate(key)}
                          />
                          <span
                            className="cp-practice-calendar__day-number"
                            data-today={key === today || undefined}
                          >
                            {date.getUTCDate()}
                          </span>
                          <span className="cp-practice-calendar__events">
                            {assignments.map((assignment) => (
                              <PortalLink
                                className="cp-practice-calendar__week-event"
                                clinicianCode={clinicianCode}
                                key={assignment.recordId}
                                path={clientHomeworkDetailPath(
                                  patientId,
                                  assignment.recordId,
                                  detailSource(assignment.calendarStatus),
                                )}
                                title={
                                  assignment.activityName || "Untitled homework"
                                }
                              >
                                <span
                                  className="cp-practice-calendar__dot"
                                  data-status={assignment.calendarStatus.toLowerCase()}
                                  aria-hidden="true"
                                />
                                <span className="cp-practice-calendar__week-event-title">
                                  {assignment.activityName ||
                                    "Untitled homework"}
                                </span>
                              </PortalLink>
                            ))}
                          </span>
                        </div>
                      );
                    }

                    return (
                      <button
                        type="button"
                        className="cp-practice-calendar__day"
                        data-outside={outsideMonth || undefined}
                        data-selected={selectedDate === key || undefined}
                        aria-pressed={selectedDate === key}
                        aria-label={dayLabel}
                        key={key}
                        onClick={() => selectDate(key)}
                      >
                        <span
                          className="cp-practice-calendar__day-number"
                          data-today={key === today || undefined}
                        >
                          {date.getUTCDate()}
                        </span>
                        <span
                          className="cp-practice-calendar__events"
                          aria-hidden="true"
                        >
                          {assignments.slice(0, 3).map((assignment) => (
                            <span
                              className="cp-practice-calendar__dot"
                              data-status={assignment.calendarStatus.toLowerCase()}
                              key={assignment.recordId}
                            />
                          ))}
                          {assignments.length > 3 ? (
                            <span className="cp-practice-calendar__more">
                              +{assignments.length - 3}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {data?.count === 0 ? (
                <EmptyState
                  title={`No homework this ${calendarView}`}
                  description="No homework practice or due dates fall within this calendar view."
                />
              ) : selectedDate ? (
                <section
                  className="cp-practice-calendar__assignments"
                  aria-labelledby="practice-calendar-date-heading"
                >
                  <div className="cp-practice-calendar__assignments-heading">
                    <CalendarDays aria-hidden="true" />
                    <div>
                      <h3 id="practice-calendar-date-heading">
                        {formatLongDate(selectedDate)}
                      </h3>
                      <p>
                        {selectedAssignments.length} homework{" "}
                        {selectedAssignments.length === 1
                          ? "assignment"
                          : "assignments"}
                      </p>
                    </div>
                  </div>
                  {selectedAssignments.length ? (
                    <div className="cp-practice-calendar__assignment-list">
                      {selectedAssignments.map((assignment) => (
                        <PortalLink
                          className="cp-practice-calendar__assignment"
                          clinicianCode={clinicianCode}
                          key={assignment.recordId}
                          path={clientHomeworkDetailPath(
                            patientId,
                            assignment.recordId,
                            detailSource(assignment.calendarStatus),
                          )}
                        >
                          <span className="cp-practice-calendar__assignment-copy">
                            <strong>
                              {assignment.activityName || "Untitled homework"}
                            </strong>
                            <span>
                              {assignment.activityType ||
                                "Activity type not provided"}
                            </span>
                          </span>
                          <span
                            className="cp-practice-calendar__status"
                            data-status={assignment.calendarStatus.toLowerCase()}
                          >
                            {assignment.calendarStatus}
                          </span>
                          <span className="cp-practice-calendar__assignment-meta">
                            {statusDetail(assignment)} · {assignment.points}{" "}
                            points · {assignment.coins} coins
                          </span>
                        </PortalLink>
                      ))}
                    </div>
                  ) : (
                    <p className="cp-practice-calendar__no-date-assignments">
                      No homework is scheduled for this date.
                    </p>
                  )}
                </section>
              ) : (
                <EmptyState
                  title="Select a date"
                  description="Choose a date to review its homework assignments."
                />
              )}
            </>
          ) : null}
        </section>
      </div>
    </ClinicianPortalLayout>
  );
}
